/**
 * GoalSettingModal Component
 *
 * Modal for setting or updating user's SAT goal score and test date.
 * Includes validation and helpful guidance.
 */

import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { X, Target, TrendingUp, AlertCircle, CheckCircle, Calendar } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('GoalSettingModal')

const SCORE_RANGES = [
  { min: 400, max: 800, label: 'Beginning', description: 'Building fundamental skills' },
  { min: 800, max: 1000, label: 'Developing', description: 'Growing proficiency' },
  { min: 1000, max: 1200, label: 'Proficient', description: 'Solid foundation' },
  { min: 1200, max: 1400, label: 'Advanced', description: 'Above average performance' },
  { min: 1400, max: 1600, label: 'Expert', description: 'Top percentile scores' }
]

const GoalSettingModal = ({
  isOpen,
  onClose,
  currentGoal,
  currentTestDate,
  predictedScore,
  onGoalSet
}) => {
  const [goalScore, setGoalScore] = useState('')
  const [testDate, setTestDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Calculate min and max dates for the date picker
  const dateConstraints = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const maxDate = new Date()
    maxDate.setFullYear(maxDate.getFullYear() + 2)
    return {
      min: tomorrow.toISOString().split('T')[0],
      max: maxDate.toISOString().split('T')[0]
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      if (currentGoal) {
        setGoalScore(currentGoal.toString())
      } else {
        // Default goal is 1600 (max score: 800 Math + 800 R&W)
        setGoalScore('1600')
      }
      if (currentTestDate) {
        // Format existing test date for input
        const date = new Date(currentTestDate)
        setTestDate(date.toISOString().split('T')[0])
      } else {
        setTestDate('')
      }
      setError(null)
    }
  }, [isOpen, currentGoal, currentTestDate])

  if (!isOpen) return null

  const scoreValue = parseInt(goalScore, 10) || 0
  const isValidScore = scoreValue >= 400 && scoreValue <= 1600
  const currentRange = SCORE_RANGES.find((r) => scoreValue >= r.min && scoreValue < r.max)

  const gap = predictedScore && scoreValue ? scoreValue - predictedScore : 0
  const isAmbitious = gap > 100 && gap <= 200
  const isVeryAmbitious = gap > 200

  // Calculate days until test for context
  const daysUntilTest = testDate
    ? Math.ceil((new Date(testDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!isValidScore) {
      setError('Please enter a valid SAT score between 400 and 1600')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const payload = {
        goalSatScore: scoreValue
      }

      // Only include testDate if provided
      if (testDate) {
        payload.testDate = testDate
      }

      const response = await api.put('/predictions/goal', payload)

      if (response.data.success) {
        toast.success('Goal set successfully!')
        if (onGoalSet) {
          onGoalSet(response.data.data)
        }
        onClose()
      } else {
        setError(response.data.message || 'Failed to set goal')
      }
    } catch (err) {
      logger.error('Error setting goal:', err)
      setError(err.response?.data?.message || 'Failed to set goal')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Set Your SAT Goal
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Current predicted score reference */}
            {predictedScore && (
              <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-primary-700 dark:text-primary-300">
                  <TrendingUp className="w-4 h-4" />
                  <span>Your predicted score: {predictedScore}</span>
                </div>
              </div>
            )}

            {/* Score input */}
            <div>
              <label
                htmlFor="goalScore"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Target SAT Score
              </label>
              <input
                type="number"
                id="goalScore"
                value={goalScore}
                onChange={(e) => setGoalScore(e.target.value)}
                placeholder="Enter score (400-1600)"
                min="400"
                max="1600"
                step="10"
                className="w-full px-4 py-3 text-lg font-semibold text-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                SAT scores range from 400 to 1600
              </p>
            </div>

            {/* Test Date input */}
            <div>
              <label
                htmlFor="testDate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  When are you taking the SAT?
                </span>
              </label>
              <input
                type="date"
                id="testDate"
                value={testDate}
                onChange={(e) => setTestDate(e.target.value)}
                min={dateConstraints.min}
                max={dateConstraints.max}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This helps us calculate your practice schedule (optional)
              </p>
              {daysUntilTest && daysUntilTest > 0 && (
                <p className="mt-1 text-xs text-primary-600 dark:text-primary-400 font-medium">
                  {daysUntilTest} days until your test
                </p>
              )}
            </div>

            {/* Score range indicator */}
            {isValidScore && currentRange && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {currentRange.label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {currentRange.description}
                  </span>
                </div>
              </div>
            )}

            {/* Gap analysis */}
            {isValidScore && predictedScore && gap !== 0 && (
              <div
                className={`p-3 rounded-lg ${
                  isVeryAmbitious
                    ? 'bg-yellow-50 dark:bg-yellow-900/20'
                    : isAmbitious
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'bg-green-50 dark:bg-green-900/20'
                }`}
              >
                <div className="flex items-start gap-2">
                  {isVeryAmbitious ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isAmbitious ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}
                    />
                  )}
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isVeryAmbitious
                          ? 'text-yellow-800 dark:text-yellow-200'
                          : isAmbitious
                            ? 'text-blue-800 dark:text-blue-200'
                            : 'text-green-800 dark:text-green-200'
                      }`}
                    >
                      {gap > 0 ? `+${gap} points` : `${gap} points`} from predicted
                    </p>
                    <p
                      className={`text-xs ${
                        isVeryAmbitious
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : isAmbitious
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-green-700 dark:text-green-300'
                      }`}
                    >
                      {isVeryAmbitious
                        ? 'Very ambitious! This will require significant focused practice.'
                        : isAmbitious
                          ? 'Ambitious goal. With consistent practice, this is achievable!'
                          : gap > 0
                            ? 'Realistic goal. Keep up your practice to reach it!'
                            : "You're already predicted to exceed this goal!"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !isValidScore}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : currentGoal ? 'Update Goal' : 'Set Goal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

GoalSettingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentGoal: PropTypes.number,
  currentTestDate: PropTypes.string,
  predictedScore: PropTypes.number,
  onGoalSet: PropTypes.func
}

export default GoalSettingModal
